/**
 * 9:16 share-card. Canvas-rendered, downloadable as PNG. Web Share API integrated.
 */
import { store } from '../store.ts';
import { TIER_DISPLAY, formatINR } from '../types.ts';
import type { WardrobeSummary } from '../types.ts';

export class AlmariShareCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;
  #current: WardrobeSummary | null = null;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      this.#current = s.summary;
      this.#render();
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(): void {
    this.#shadow.innerHTML = /* html */ `
<style>
:host {
  display: grid;
  gap: 10px;
}
button {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  padding: 16px;
  background: var(--ink);
  color: var(--paper);
  border-radius: 8px;
  font-weight: 700;
}
button:active { filter: brightness(1.05); }
button.ghost {
  background: transparent;
  color: var(--ink-dim);
  border: 1px solid var(--rule);
}
</style>
<button id="share">Share almari card</button>
<button class="ghost" id="download">Download 9:16 PNG</button>
`;
    this.#shadow.getElementById('share')?.addEventListener('click', () => this.#share());
    this.#shadow.getElementById('download')?.addEventListener('click', () => this.#download());
  }

  async #renderCanvas(): Promise<Blob | null> {
    if (!this.#current) return null;
    const W = 1080;
    const H = 1920;
    const canvas = new OffscreenCanvas(W, H);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const s = this.#current;
    const tier = s.tier;
    const meta = TIER_DISPLAY[tier];

    ctx.fillStyle = '#f3e7c8';
    ctx.fillRect(0, 0, W, H);

    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, 'rgba(184, 142, 58, 0.18)');
    grad.addColorStop(1, 'rgba(243, 231, 200, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = '#722f37';
    ctx.font = '700 60px "Cormorant Garamond", serif';
    ctx.textAlign = 'left';
    ctx.fillText('अलमारी', 80, 200);

    ctx.fillStyle = 'rgba(114, 47, 55, 0.55)';
    ctx.font = '500 22px "Space Mono", monospace';
    ctx.fillText('ALMARI · WARDROBE LEDGER', 80, 240);

    ctx.fillStyle = '#b88e3a';
    ctx.font = '500 32px "Space Mono", monospace';
    ctx.fillText('TIER', 80, 420);

    ctx.fillStyle = '#722f37';
    ctx.font = 'italic 700 100px "Cormorant Garamond", serif';
    ctx.fillText(meta.name, 80, 540);

    ctx.fillStyle = 'rgba(114, 47, 55, 0.7)';
    ctx.font = 'italic 500 34px "Cormorant Garamond", serif';
    ctx.fillText(`"${meta.tagline}"`, 80, 600);

    ctx.fillStyle = '#b88e3a';
    ctx.font = '500 24px "Space Mono", monospace';
    ctx.fillText('CURRENT VALUE', 80, 780);

    ctx.fillStyle = '#722f37';
    ctx.font = '700 140px "Cormorant Garamond", serif';
    ctx.fillText(formatINR(s.total_current_value_inr), 80, 920);

    ctx.fillStyle = 'rgba(114, 47, 55, 0.6)';
    ctx.font = '500 28px "Space Mono", monospace';
    ctx.fillText(`${s.total_items} PIECES · PAID ${formatINR(s.total_value_inr)}`, 80, 980);

    const topBrands = Object.entries(s.by_brand).slice(0, 4);
    ctx.fillStyle = '#b88e3a';
    ctx.font = '500 24px "Space Mono", monospace';
    ctx.fillText('TOP LABELS', 80, 1180);
    let y = 1240;
    for (const [name, stat] of topBrands) {
      ctx.fillStyle = '#722f37';
      ctx.font = 'italic 500 44px "Cormorant Garamond", serif';
      ctx.fillText(name, 80, y);
      ctx.fillStyle = 'rgba(114, 47, 55, 0.55)';
      ctx.font = '700 36px "Cormorant Garamond", serif';
      ctx.textAlign = 'right';
      ctx.fillText(formatINR(stat.value), W - 80, y);
      ctx.textAlign = 'left';
      y += 64;
    }

    if (s.insights.most_worn) {
      ctx.fillStyle = '#b88e3a';
      ctx.font = '500 24px "Space Mono", monospace';
      ctx.fillText('BEST ROI · COST/WEAR', 80, 1620);
      ctx.fillStyle = '#722f37';
      ctx.font = '700 72px "Cormorant Garamond", serif';
      ctx.fillText(formatINR(s.insights.most_worn.cost_per_wear), 80, 1700);
      ctx.fillStyle = 'rgba(114, 47, 55, 0.65)';
      ctx.font = 'italic 500 28px "Cormorant Garamond", serif';
      ctx.fillText(`${s.insights.most_worn.brand} · ${s.insights.most_worn.wear_count} wears`, 80, 1740);
    }

    ctx.fillStyle = 'rgba(114, 47, 55, 0.4)';
    ctx.font = '500 20px "Space Mono", monospace';
    ctx.fillText('almari.yourtube.in', 80, H - 100);
    ctx.fillStyle = '#b88e3a';
    ctx.fillText('▣', W - 110, H - 100);

    return await canvas.convertToBlob({ type: 'image/png' });
  }

  async #share(): Promise<void> {
    const blob = await this.#renderCanvas();
    if (!blob) return;
    const file = new File([blob], 'almari-kahaani.png', { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: 'My Almari', text: 'My wardrobe ledger' });
      } catch {
        this.#download(blob);
      }
    } else {
      this.#download(blob);
    }
  }

  async #download(b?: Blob): Promise<void> {
    const blob = b ?? await this.#renderCanvas();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'almari-kahaani.png';
    a.click();
    URL.revokeObjectURL(url);
  }
}

customElements.define('almari-share-card', AlmariShareCard);
